import { noop } from 'lodash';
import styled from 'styled-components';

import CaseActionModalV2 from 'dashboard/components/CaseActionModal/CaseActionModalV2';
import { useComputedProperty } from 'dashboard/components/GridLayoutRender/computed-properties/useComputedProperty';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { Account } from 'dashboard/models/account';
import { Case, CaseActionWorkflow, CaseDataManager, CaseObject } from 'dashboard/models/case';
import { Inquiry } from 'dashboard/models/inquiry';
import { Verification } from 'dashboard/models/verification';
import { ModuleButton } from 'dashboard/modules/Button/model';
import { default as ButtonComponent } from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { Json } from 'lib/types';

interface ButtonProps {
  module: ModuleButton;
  case: Case;
  caseObjects: CaseObject[];
  caseActionWorkflow?: CaseActionWorkflow;
  caseDataManager: CaseDataManager;
  runCodeWithContext: (code: string) => Promise<unknown>;
  handleRunCaseActionWorkflow: (values: Json, workflowId: string) => Promise<void>;
  isPreview?: boolean;
}

const ButtonCard = styled(Card)`
  margin-bottom: 0;
  height: 100%;
`;

const Button = ({
  module,
  case: kase,
  caseObjects,
  caseActionWorkflow,
  caseDataManager,
  isPreview = false,
  runCodeWithContext,
  handleRunCaseActionWorkflow,
}: ButtonProps) => {
  const [isEnabled] = useComputedProperty<boolean>(
    runCodeWithContext,
    module['module-layout'].enabled,
    true,
  );

  const accounts = caseObjects.filter((object) => object.type === ApiModel.Account) as Account[];
  const inquiries = caseObjects.filter((object) => object.type === ApiModel.Inquiry) as Inquiry[];
  const verifications = caseObjects.filter((object) =>
    [
      ApiModel.VerificationAamva,
      ApiModel.VerificationDatabase,
      ApiModel.VerificationDatabaseBusiness,
      ApiModel.VerificationDatabaseStandard,
      ApiModel.VerificationDatabaseEcbsv,
      ApiModel.VerificationDatabasePhoneCarrier,
      ApiModel.VerificationDatabaseTin,
      ApiModel.VerificationDocument,
      ApiModel.VerificationGovernmentId,
      ApiModel.VerificationPhoneNumber,
      ApiModel.VerificationSelfie,
    ].includes(object.type),
  ) as Verification[];

  const [workflowModal, { enable: showWorkflowModal, disable: hideWorkflowModal }] =
    useToggle(false);

  const onClick = () => {
    if (caseActionWorkflow?.trigger?.attributes?.['param-schema']?.length === 0) {
      handleRunCaseActionWorkflow({}, caseActionWorkflow.workflow.id);
    } else {
      showWorkflowModal();
    }
  };

  return (
    <ButtonCard>
      <ButtonComponent
        id={`module__button--${module.token}`}
        data-test={`module__button--${module.token}`}
        style={{ width: '100%', height: '100%' }}
        // We dont disable button in preview mode to propagate the click event to upper level to display control panel
        disabled={!isPreview && (!isEnabled || !caseActionWorkflow)}
        onClick={isPreview ? noop : onClick}
        variant={module.variant ?? 'primary'}
      >
        {module.name}
      </ButtonComponent>
      <ModalWrapper isOpen={workflowModal} handleCloseModal={hideWorkflowModal}>
        {caseActionWorkflow ? (
          <CaseActionModalV2
            case={kase}
            accounts={accounts}
            inquiries={inquiries}
            verifications={verifications}
            caseDataManager={caseDataManager}
            caseAction={caseActionWorkflow}
            handleRunWorkflow={handleRunCaseActionWorkflow}
            hideModal={hideWorkflowModal}
          />
        ) : (
          <Paragraph>Please configure a case action to trigger.</Paragraph>
        )}
      </ModalWrapper>
    </ButtonCard>
  );
};

export default Button;
